import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchMyPermissions } from '../services/Http/Api';
import { useAuth } from '../context/AuthProvider';

const PermissionContext = createContext({
    permissions: {},
    loading: true,
    hasPermission: () => false,
    canRead: () => false,
    canWrite: () => false,
    canDelete: () => false,
    refreshPermissions: () => {},
});

// Permission level hierarchy: delete > write > read > none
const PERMISSION_LEVELS = {
    none: 0,
    read: 1,
    write: 2,
    delete: 3,
};

export const PermissionProvider = ({ children }) => {
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(true);
    const { isAuthenticated, user } = useAuth();

    const loadPermissions = useCallback(async () => {
        // Check if user is logged in
        if (!isAuthenticated) {
            setPermissions({});
            setLoading(false);
            return;
        }

        if (user?.is_system_admin) {
            // System admins are handled as platform superusers in the frontend.
            // Skip organization-scoped permission endpoint to avoid unnecessary calls.
            setPermissions({});
            setLoading(false);
            return;
        }

        try {
            const perms = await fetchMyPermissions();
            setPermissions(perms || {});
        } catch (error) {
            console.error('Error fetching permissions:', error);
            setPermissions({});
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, user]);

    // Reload permissions when auth state changes
    useEffect(() => {
        loadPermissions();
    }, [loadPermissions, isAuthenticated, user]);

    // Check if the user's permission level for a module meets the required level
    const hasPermission = useCallback((module, requiredLevel) => {
        if (user?.is_system_admin) {
            return true;
        }
        const userLevel = permissions[module] || 'none';
        return PERMISSION_LEVELS[userLevel] >= PERMISSION_LEVELS[requiredLevel];
    }, [permissions, user]);

    // Convenience methods
    const canRead = useCallback((module) => hasPermission(module, 'read'), [hasPermission]);
    const canWrite = useCallback((module) => hasPermission(module, 'write'), [hasPermission]);
    const canDelete = useCallback((module) => hasPermission(module, 'delete'), [hasPermission]);

    const refreshPermissions = useCallback(() => {
        setLoading(true);
        loadPermissions();
    }, [loadPermissions]);

    const value = {
        permissions,
        loading,
        hasPermission,
        canRead,
        canWrite,
        canDelete,
        refreshPermissions,
    };

    return (
        <PermissionContext.Provider value={value}>
            {children}
        </PermissionContext.Provider>
    );
};

// Custom hook for using permissions
export const usePermissions = () => {
    const context = useContext(PermissionContext);
    if (!context) {
        throw new Error('usePermissions must be used within a PermissionProvider');
    }
    return context;
};

// HOC for permission-protected components
export const withPermission = (WrappedComponent, module, requiredLevel = 'read') => {
    return function PermissionProtectedComponent(props) {
        const { hasPermission, loading } = usePermissions();

        if (loading) {
            return null; // Or a loading spinner
        }

        if (!hasPermission(module, requiredLevel)) {
            return null; // Or an "Access Denied" component
        }

        return <WrappedComponent {...props} />;
    };
};

// Component for conditional rendering based on permissions
export const PermissionGate = ({ module, requiredLevel = 'read', children, fallback = null }) => {
    const { hasPermission, loading } = usePermissions();

    if (loading) {
        return null;
    }

    if (!hasPermission(module, requiredLevel)) {
        return fallback;
    }

    return children;
};

export default PermissionProvider;
